/**
 * AI Scribe — generated-note socket
 *
 * Owns the transcription websocket so both the V1 modal content and the V2
 * docked window render the same note from the same source, and neither has to
 * know the transport exists.
 *
 * @author [Rajkumar Rathod]
 */

import { useCallback, useEffect, useRef, useState } from "react";
import useWebSocket from "@/hooks/use-web-socket";
import { BASE_PATH } from "@/services/base";
import {
  NOTE_STREAM_FALLBACK_TIMEOUT_MS,
  NOTE_STREAM_NON_DATA_TYPES,
  NOTE_STREAM_PENDING_STATUSES,
  NOTE_STREAM_SOCKET_PATH,
  NOTE_STREAM_STATUS,
} from "../ai-scriber-constant";
import { ScribeNoteData, ScribeNoteStream } from "../ai-scriber-types";

interface UseScribeNoteStreamOptions {
  /** Fired once with every note the socket delivers. */
  onNoteReceived?: (data: ScribeNoteData) => void;
}

const buildSocketUrl = () =>
  `wss://${BASE_PATH?.split("//")?.[1]}${NOTE_STREAM_SOCKET_PATH}`;

export const useScribeNoteStream = ({
  onNoteReceived,
}: UseScribeNoteStreamOptions = {}): ScribeNoteStream => {
  const [noteData, setNoteData] = useState<ScribeNoteData>();
  const [isWaiting, setIsWaiting] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [streamStatus, setStreamStatus] = useState<string>(
    NOTE_STREAM_STATUS.IN_PROGRESS
  );

  /** Kept in a ref so a new inline callback never re-opens the socket. */
  const onNoteReceivedRef = useRef(onNoteReceived);
  onNoteReceivedRef.current = onNoteReceived;

  const handleMessageReceived = useCallback((event: MessageEvent<string>) => {
    if (!event?.data) {
      setIsWaiting(true);
      return;
    }

    const message = JSON.parse(event.data || "{}");
    const type: string = message?.type || NOTE_STREAM_STATUS.FAILED;

    setStreamStatus(type);
    setIsGenerating(type === NOTE_STREAM_STATUS.IN_PROGRESS);
    setIsWaiting(NOTE_STREAM_PENDING_STATUSES.includes(type));

    if (NOTE_STREAM_NON_DATA_TYPES.includes(type as never)) return;

    setIsWaiting(false);
    setNoteData(message?.data);
    onNoteReceivedRef.current?.(message?.data);
  }, []);

  const { error: socketError } = useWebSocket<string>({
    url: buildSocketUrl(),
    onMessageReceived: handleMessageReceived,
  });

  const isServerGenerating = isGenerating && !socketError;

  /** LLM generation outlasts the fallback, so it only bounds a silent or dead socket. */
  useEffect(() => {
    if (!isWaiting || isServerGenerating) return;
    const timeoutId = window.setTimeout(
      () => setIsWaiting(false),
      NOTE_STREAM_FALLBACK_TIMEOUT_MS
    );
    return () => window.clearTimeout(timeoutId);
  }, [isWaiting, isServerGenerating]);

  const markRetrying = useCallback(() => {
    setStreamStatus(NOTE_STREAM_STATUS.IN_PROGRESS);
    setIsGenerating(false);
    setIsWaiting(true);
  }, []);

  const reset = useCallback(() => {
    setNoteData(undefined);
    setStreamStatus(NOTE_STREAM_STATUS.IN_PROGRESS);
    setIsWaiting(true);
  }, []);

  return {
    noteData,
    isLoading: !noteData && isWaiting,
    isError: streamStatus === NOTE_STREAM_STATUS.FAILED && !noteData,
    markRetrying,
    reset,
  };
};

export default useScribeNoteStream;
